import { IconProp } from "@fortawesome/fontawesome-svg-core"
import clsx from "clsx"
import { useMemo } from "react"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea } from "@particles/index"

import LinkButton from "@atoms/LinkButton/LinkButton"
import { LinkButtonColorScheme } from "@atoms/LinkButton/LinkButton.types"
import { Icon, IconButton } from "@atoms/index"

import styles from "./AlertBanner.module.scss"
import { AlertBannerProps, AlertBannerType, TipBannerType } from "./AlertBanner.types"

export default function AlertBanner(props: AlertBannerProps) {
    const { type, orientation = "horizontal", status, message, fullWidth = false, className } = props

    const title: TipBannerType["title"] = (props as TipBannerType)?.title
    const cta: AlertBannerType["cta"] = (props as AlertBannerType)?.cta
    const isDismissable: AlertBannerType["isDismissable"] = (props as AlertBannerType)?.isDismissable
    const onDismiss: AlertBannerType["onDismiss"] = (props as AlertBannerType)?.onDismiss

    const typeIcon = useMemo(() => {
        const typeMap: { [key in AlertBannerProps["status"]]: IconProp } = {
            info: faIcon({ name: "circle-info", style: "solid" }),
            warning: faIcon({ name: "triangle-exclamation", style: "solid" }),
            error: faIcon({ name: "circle-xmark", style: "solid" }),
            success: faIcon({ name: "circle-check", style: "solid" }),
        }
        return typeMap[status]
    }, [status])

    const ctaColorScheme = useMemo(() => {
        const colorSchemeMap: { [key in AlertBannerProps["status"]]: LinkButtonColorScheme } = {
            info: "blue",
            warning: "amber",
            error: "tomato",
            success: "jade",
        }
        return colorSchemeMap[status]
    }, [status])

    const iconSize = 14

    const renderedIcon = useMemo(() => {
        if (type === "tip") {
            return <span className={styles.indicator} />
        } else {
            return <Icon icon={typeIcon} size={iconSize} />
        }
    }, [type, typeIcon, iconSize])

    return (
        <div
            role="alert"
            aria-live="polite"
            className={clsx(styles.base, styles[status], styles[type], styles[orientation], className, {
                [styles.fullWidth]: fullWidth === true,
            })}
        >
            <div className={styles.iconContainer}>{renderedIcon}</div>
            <div className={styles.content}>
                <div className={styles.text}>
                    {title && <span className={styles.title}>{title}</span>} {message}
                </div>
                {cta && (
                    <ExtraTouchArea extraTouchArea={8}>
                        <LinkButton {...cta} colorScheme={ctaColorScheme} className={styles.cta} />
                    </ExtraTouchArea>
                )}
            </div>
            {isDismissable && (
                <ExtraTouchArea extraTouchArea={7}>
                    <IconButton
                        size="xs"
                        variant="muted"
                        icon={faIcon({ name: "xmark", style: "regular" })}
                        onClick={onDismiss}
                        className={styles.dismissButton}
                    />
                </ExtraTouchArea>
            )}
        </div>
    )
}
